import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useCallback, memo } from "react"
import Loader from "../../../components/Loader"
import Pagination from "../../../components/Pagination"
import Filter from "../../../components/TableFilters"
import { formatISODate } from "../../../utils/datetime"
import { useNavigate } from "react-router-dom"
import routePaths from "../../../constants/path"
import { bodyParts } from "../../../constants"

const columnHelper = createColumnHelper()

const ErrorlogsTable = ({
  data,
  loading,
  onPaginationChange,
  pagination,
  pageCount,
  filters,
  setFilters,
  query,
}) => {
  const navigate = useNavigate()

  const handleViewDetails = useCallback(
    (rowData) => {
      navigate(routePaths.errorReports, {
        state: { ...query, ...rowData },
      })
    },
    [navigate, query]
  )

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("messageType", {
        header: "Status Code",
        id: "messageType",
        cell: (info) => info.renderValue() || "N/A",
        sortDescFirst: false,
        size: 150,
        meta: {
          filterVariant: "text",
        },
      }),
      columnHelper.accessor("errorDesc", {
        header: "Status Reason",
        id: "errorDesc",
        size: 150,
        cell: (info) => info.renderValue() || "N/A",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("occurrences", {
        header: "# of Occurrences",
        id: "occurrences",
        size: 60,
        cell: (info) => info.renderValue() || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("lastOccurrence", {
        header: "Last Occurrence",
        id: "lastOccurrence",
        size: 100,
        cell: (info) => formatISODate(info.renderValue()) || "",
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor("Actions", {
        header: "Actions",
        id: "actions",
        size: 120,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-center">
            <button
              className="bg-secondary hover:bg-secondary_dark px-2 py-1 text-sm text-white rounded"
              onClick={() => handleViewDetails(row.original)}
            >
              View Details
            </button>
          </div>
        ),
        sortDescFirst: false,
        enableColumnFilter: false,
      }),
    ]

    if (
      [
        bodyParts.SPINE,
        bodyParts.KNEE,
        bodyParts.SHOULDER,
        bodyParts.BRAIN,
      ].includes(query.bodyPart)
    ) {
      baseColumns.splice(
        2,
        0,
        columnHelper.accessor("viewType", {
          header: query.bodyPart === bodyParts.BRAIN ? "Type" : "View",
          id: "viewType",
          size: 80,
          cell: (info) => info.renderValue() || "N/A",
          sortDescFirst: false,
          enableColumnFilter: false,
        })
      )
    }

    return baseColumns
  }, [handleViewDetails, query.bodyPart])

  const tableConfig = useMemo(
    () => ({
      columns,
      data,
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange,
      onColumnFiltersChange: setFilters,
      pageCount,
      state: {
        pagination,
        columnFilters: filters,
      },
    }),
    [
      columns,
      data,
      onPaginationChange,
      setFilters,
      pageCount,
      pagination,
      filters,
    ]
  )

  const table = useReactTable(tableConfig)

  const TableHeader = useMemo(
    () => (
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((column) => (
              <th
                key={column.id}
                className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                style={{ width: `${column.getSize()}px` }}
              >
                <div className="flex flex-col justify-start items-start">
                  <button
                    {...{
                      className: column.column.getCanSort()
                        ? "w-full select-none cursor-pointer flex items-center gap-1"
                        : "w-full",
                      onClick: column.column.getToggleSortingHandler(),
                    }}
                  >
                    {flexRender(
                      column.column.columnDef.header,
                      column.getContext()
                    )}
                    {{
                      asc: <SortAscending className="h-4 w-4" />,
                      desc: <SortDescending className="h-4 w-4" />,
                    }[column.column.getIsSorted()] ?? null}
                  </button>
                  {column.column.getCanFilter() ? (
                    <div className="mt-2">
                      <Filter column={column.column} />
                    </div>
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
    ),
    [table, query.bodyPart]
  )

  const TableBody = useMemo(() => {
    if (loading) {
      return (
        <tbody>
          <tr>
            <td colSpan={table.getAllColumns().length}>
              <div className="py-2">
                <Loader />
              </div>
            </td>
          </tr>
        </tbody>
      )
    }

    return (
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id} className="hover:bg-gray-100">
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="px-4 py-2 border-b border-gray-200">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    )
  }, [loading, table, data])

  return (
    <>
      <div>
        <table className="min-w-full bg-white border border-gray-200">
          {TableHeader}
          {TableBody}
        </table>
        {!loading && data.length > 0 && <Pagination tableLib={table} />}
        {!loading && data.length === 0 && (
          <div className="mt-3 text-center">No Logs found</div>
        )}
      </div>
    </>
  )
}

export default memo(ErrorlogsTable)
